import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { AdminDocumentation } from "../AdminDocumentation";
import { DisabledUsersManagement } from "./DisabledUsersManagement";
import { DoctorInvitationsManagement } from "./DoctorInvitationsManagement";
import { StaticDoctorDirectoryManagement } from "./StaticDoctorDirectoryManagement";
import { UnverifiedUsersManagement } from "./UnverifiedUsersManagement";

type Tab =
  | "doctor-invitations"
  | "static-doctors"
  | "disabled-users"
  | "unverified-users"
  | "docs-tools";

const TABS: { id: Tab; labelKey: string }[] = [
  {
    id: "doctor-invitations",
    labelKey: "admin.userGovernance.tabs.doctorInvitations",
  },
  {
    id: "static-doctors",
    labelKey: "admin.userGovernance.tabs.staticDoctors",
  },
  { id: "disabled-users", labelKey: "admin.userGovernance.tabs.disabledUsers" },
  {
    id: "unverified-users",
    labelKey: "admin.userGovernance.tabs.unverifiedUsers",
  },
  { id: "docs-tools", labelKey: "admin.userGovernance.tabs.docsTools" },
];

export const UserGovernance: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as Tab) || "doctor-invitations";

  const handleTabChange = (tab: Tab) => {
    setSearchParams({ tab });
  };

  return (
    <div className="page-container">
      <div className="flex justify-between items-center page-header">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t("admin.userGovernance.title")}
          </h2>
        </div>
      </div>

      <div className="border-b border-neutral-200 -mt-4">
        <nav className="-mb-px flex space-x-1 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-ews-primary text-ews-primary"
                  : "border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300"
              }`}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </nav>
      </div>

      <div>
        {activeTab === "doctor-invitations" && <DoctorInvitationsManagement />}
        {activeTab === "static-doctors" && <StaticDoctorDirectoryManagement />}
        {activeTab === "disabled-users" && <DisabledUsersManagement />}
        {activeTab === "unverified-users" && <UnverifiedUsersManagement />}
        {activeTab === "docs-tools" && <AdminDocumentation />}
      </div>
    </div>
  );
};
